import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { useBrandColors } from "@/lib/brand-colors";

interface ThermostatReadingPoint {
  recordedAt: string;
  temperatureF: number | null;
  heatSetPointF?: number | null;
  coolSetPointF?: number | null;
}

interface Props {
  data: ThermostatReadingPoint[];
  height?: number;
}

const SERIES_LABELS: Record<string, string> = {
  temperatureF: "Temperature",
  heatSetPointF: "Heat setpoint",
  coolSetPointF: "Cool setpoint",
};

export function ThermostatTrendChart({ data, height = 160 }: Props) {
  const brandColors = useBrandColors();
  const points = data
    .filter(
      (p) =>
        (p.temperatureF !== null && Number.isFinite(p.temperatureF)) ||
        (p.heatSetPointF != null && Number.isFinite(p.heatSetPointF)) ||
        (p.coolSetPointF != null && Number.isFinite(p.coolSetPointF)),
    )
    .map((p) => ({
      ts: new Date(p.recordedAt).getTime(),
      label: new Date(p.recordedAt).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
      }),
      temperatureF:
        p.temperatureF !== null && Number.isFinite(p.temperatureF) ? (p.temperatureF as number) : null,
      heatSetPointF:
        p.heatSetPointF != null && Number.isFinite(p.heatSetPointF) ? (p.heatSetPointF as number) : null,
      coolSetPointF:
        p.coolSetPointF != null && Number.isFinite(p.coolSetPointF) ? (p.coolSetPointF as number) : null,
    }));
  if (points.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-gray-400 border border-dashed rounded"
        style={{ height }}
        data-testid="text-no-readings"
      >
        No temperature readings yet
      </div>
    );
  }
  const hasTemperature = points.some((p) => p.temperatureF !== null);
  const hasHeat = points.some((p) => p.heatSetPointF !== null);
  const hasCool = points.some((p) => p.coolSetPointF !== null);
  const allValues: number[] = [];
  for (const p of points) {
    if (p.temperatureF !== null) allValues.push(p.temperatureF);
    if (p.heatSetPointF !== null) allValues.push(p.heatSetPointF);
    if (p.coolSetPointF !== null) allValues.push(p.coolSetPointF);
  }
  const min = Math.floor(Math.min(...allValues) - 1);
  const max = Math.ceil(Math.max(...allValues) + 1);
  return (
    <div style={{ height }} data-testid="chart-thermostat-trend">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: "#64748b" }}
            interval="preserveStartEnd"
            minTickGap={32}
          />
          <YAxis
            domain={[min, max]}
            tick={{ fontSize: 10, fill: "#64748b" }}
            width={32}
            tickFormatter={(v) => `${v}°`}
          />
          <Tooltip
            contentStyle={{ fontSize: 12, padding: "4px 8px" }}
            formatter={(value: number, name: string) => [
              `${value.toFixed(1)}°F`,
              SERIES_LABELS[name] ?? name,
            ]}
            labelFormatter={(label) => label}
          />
          <Legend
            verticalAlign="bottom"
            height={20}
            iconType="plainline"
            wrapperStyle={{ fontSize: 10, color: "#64748b" }}
          />
          {hasTemperature && (
            <Line
              type="monotone"
              dataKey="temperatureF"
              name={SERIES_LABELS.temperatureF}
              stroke={brandColors.brand}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          )}
          {hasHeat && (
            <Line
              type="monotone"
              dataKey="heatSetPointF"
              name={SERIES_LABELS.heatSetPointF}
              stroke="#f97316"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          )}
          {hasCool && (
            <Line
              type="monotone"
              dataKey="coolSetPointF"
              name={SERIES_LABELS.coolSetPointF}
              stroke="#3b82f6"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
